import { type ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { styles } from './menu.styles';
export interface MenuItem {
  key: string;
  label: ComponentChildren;
  /** Optional leading icon. */
  icon?: ComponentChildren;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}
interface MenuProps {
  /** Renders the clickable trigger; wire the button's onClick to `toggle` and
   *  spread `buttonProps` on it for the ARIA menu-button contract. */
  trigger: (ctx: {
    open: boolean;
    toggle: () => void;
    buttonProps: {
      'aria-haspopup': 'menu';
      'aria-expanded': boolean;
    };
  }) => ComponentChildren;
  items: MenuItem[];
  /** Which edge the dropdown aligns to (default left). */
  align?: 'left' | 'right';
}

/** A click-to-open action menu (trigger + outside-click-close + item list). Shared
 *  so SiteSwitcher / the Table column menu and new callers don't each hand-roll it. */
export function Menu({
  trigger,
  items,
  align = 'left'
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemEls = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);
  const focusTrigger = () => wrapRef.current?.querySelector('button')?.focus();
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        focusTrigger();
        return;
      }
      // APG menu pattern: arrows walk the items (wrapping), Home/End jump.
      const els = itemEls();
      if (els.length === 0) return;
      const i = els.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        els[(i + 1) % els.length]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        els[(i - 1 + els.length) % els.length]?.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        els[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        els[els.length - 1]?.focus();
      }
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    // Menu-button pattern: opening moves focus to the first item.
    itemEls()[0]?.focus();
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const toggle = () => setOpen(o => !o);
  return <div ref={wrapRef} {...stylex.props(styles.wrap)} onClick={e => e.stopPropagation()}>
      {trigger({
      open,
      toggle,
      buttonProps: {
        'aria-haspopup': 'menu',
        'aria-expanded': open
      }
    })}
      {open && <div ref={menuRef} role="menu" {...stylex.props(styles.menu, align === 'right' ? styles.alignRight : styles.alignLeft)}>
          {items.map(it => <button key={it.key} type="button" role="menuitem" disabled={it.disabled} onClick={() => {
        if (it.disabled) return;
        setOpen(false);
        it.onSelect();
        focusTrigger();
      }} {...stylex.props(styles.item, it.danger && styles.itemDanger, it.disabled && styles.itemDisabled)}>
              {it.icon != null && <span {...stylex.props(styles.icon)}>{it.icon}</span>}
              <span {...stylex.props(styles.label)}>{it.label}</span>
            </button>)}
        </div>}
    </div>;
}